const { isInterventionCancelledBeforeStart } = require('./interventionCancellation');
// Shared interpretation for persisted intervention records in API and browser.
// The canonical lists remain the allowed workflow-write vocabulary; historical
// read states are preserved separately and never default to active work.
const CANONICAL_INTERVENTION_STATUSES = Object.freeze([
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
  "approved",
  "rejected",
  "in_progress",
  "suspended",
  "completed",
  "cancelled",
]);

const CANONICAL_INTERVENTION_REVIEW_STATUSES = Object.freeze([
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
  "approved",
  "rejected",
]);

const CANONICAL_INTERVENTION_DELIVERY_STATUSES = Object.freeze([
  "planned",
  "in_progress",
  "suspended",
  "completed",
  "cancelled",
]);

const CANONICAL_INTERVENTION_STATUS_SET = new Set(CANONICAL_INTERVENTION_STATUSES);
const CANONICAL_INTERVENTION_REVIEW_STATUS_SET = new Set(CANONICAL_INTERVENTION_REVIEW_STATUSES);
const CANONICAL_INTERVENTION_DELIVERY_STATUS_SET = new Set(CANONICAL_INTERVENTION_DELIVERY_STATUSES);

const normalizeWorkflowKey = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");


const normalizeInterventionStatus = (value, fallback = null) => {
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CANONICAL_INTERVENTION_STATUS_SET.has(normalized) ? normalized : fallback;
};

const normalizeInterventionReviewStatus = (value, fallback = null) => {
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CANONICAL_INTERVENTION_REVIEW_STATUS_SET.has(normalized) ? normalized : fallback;
};

const normalizeInterventionDeliveryStatus = (value, fallback = null) => {
  if (value === null || typeof value === "undefined") return fallback;
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return CANONICAL_INTERVENTION_DELIVERY_STATUS_SET.has(normalized) ? normalized : fallback;
};

const resolveInterventionStateFields = (record = {}, { fallbackStatus = null } = {}) => {
  const source =
    record && typeof record === "object"
      ? record
      : { status: record };
  // Retired records remain readable history. They are not writable workflow
  // statuses and must never acquire a draft/planned fallback or stale delivery.
  const storedStatus = normalizeWorkflowKey(
    source.status ?? source.interventionStatus ?? source.intervention_status
  );
  const retiredProposal = normalizeWorkflowKey(source.proposalReviewStatus ?? source.proposal_review_status) === "withdrawn" &&
    ["draft", "submitted", "in_review", "changes_requested"].includes(storedStatus);
  if (["archived", "withdrawn"].includes(storedStatus) || retiredProposal) {
    const historicalStatus = retiredProposal ? "withdrawn" : storedStatus;
    return {
      status: historicalStatus,
      legacyStatus: historicalStatus,
      reviewStatus: null,
      review_status: null,
      proposalReviewStatus: null,
      proposal_review_status: null,
      deliveryStatus: null,
      delivery_status: null,
      effectiveStatus: historicalStatus,
      effective_status: historicalStatus,
    };
  }
  const legacyStatus =
    normalizeInterventionStatus(
      source?.status ??
        source?.interventionStatus ??
        source?.intervention_status ??
        null,
      null
    ) || normalizeInterventionStatus(fallbackStatus, null);
  const explicitReviewStatus =
    normalizeInterventionReviewStatus(
      source?.reviewStatus ??
        source?.review_status ??
        source?.proposalReviewStatus ??
        source?.proposal_review_status ??
        null,
      null
    );
  const explicitDeliveryStatus =
    normalizeInterventionDeliveryStatus(
      source?.deliveryStatus ?? source?.delivery_status ?? null,
      null
    );

  let reviewStatus = explicitReviewStatus;
  let deliveryStatus = explicitDeliveryStatus;

  if (!reviewStatus) {
    if (
      legacyStatus &&
      ["draft", "submitted", "in_review", "changes_requested", "approved", "rejected"].includes(legacyStatus)
    ) {
      reviewStatus = legacyStatus;
    } else if (
      legacyStatus &&
      ["in_progress", "suspended", "completed", "cancelled"].includes(legacyStatus)
    ) {
      reviewStatus = "approved";
    }
  }

  if (!deliveryStatus) {
    if (legacyStatus === "approved") {
      deliveryStatus = "planned";
    } else if (
      legacyStatus &&
      ["in_progress", "suspended", "completed", "cancelled"].includes(legacyStatus)
    ) {
      deliveryStatus = legacyStatus;
    }
  }

  const effectiveStatus =
    (deliveryStatus === "planned" ? "approved" : deliveryStatus) ||
    reviewStatus ||
    legacyStatus ||
    normalizeInterventionStatus(fallbackStatus, null) ||
    null;

  return {
    status: legacyStatus || null,
    legacyStatus: legacyStatus || null,
    reviewStatus: reviewStatus || null,
    review_status: reviewStatus || null,
    proposalReviewStatus: reviewStatus || null,
    proposal_review_status: reviewStatus || null,
    deliveryStatus: deliveryStatus || null,
    delivery_status: deliveryStatus || null,
    effectiveStatus,
    effective_status: effectiveStatus,
  };
};


// A proposal that never entered delivery, or retained retired history, cannot
// acquire a delivery-outcome requirement merely because a plan is being closed.
const getInterventionsMissingDeliveryOutcomes = records => (Array.isArray(records) ? records : []).filter(record => {
  if (isInterventionCancelledBeforeStart(record)) return false;
  const state = resolveInterventionStateFields(record);
  if (["draft", "rejected", "archived", "withdrawn"].includes(state.effectiveStatus)) return false;
  return !String(record?.outcome ?? record?.outcome_code ?? "").trim();
});

const getInterventionDeliveryEndDates = records => (Array.isArray(records) ? records : [])
  .filter(record => !isInterventionCancelledBeforeStart(record))
  .filter(record => ["completed", "cancelled"].includes(resolveInterventionStateFields(record).deliveryStatus))
  .map(record => record?.endDate ?? record?.end_date ?? null)
  .filter(Boolean);

module.exports = { resolveInterventionStateFields, getInterventionsMissingDeliveryOutcomes, getInterventionDeliveryEndDates };
